// University of Nevada, Reno: public college page, official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UNR's Common Data Set 2025-2026, linked from the Data Den's Common Data
//    Set page (Office of Institutional Analysis): C1 applicants, admits and
//    enrollees for Fall 2025 (the residency table gives totals only), C8 test
//    policy for Fall 2027. The C21/C22 early decision and early action items
//    are left blank, and G5 (books, transportation, other) is blank.
//  - UNR Admissions, Freshman Admissions Guide: the Fall 2027 deadline table,
//    the 3.0 weighted academic GPA requirement, the test score FAQ, no
//    application fee for domestic freshmen. No decision dates are published.
//  - UNR Financial Aid, 2026-2027 cost of attendance tables (Nevada resident,
//    non-resident and WUE, full time, living on campus), with UNR's own
//    totals; the source link is the Cost of Attendance page that links them.
//    Departure: these are used instead of the CDS because G5 is blank, and
//    the CDS G1 tuition ($8,656 and $27,288) is lower than the 2026-2027
//    tuition on the cost tables and the Tuition and Fees page ($9,383 base).
//  - College Scorecard API, school id 182290 (net price by family income).
// Testing: the admissions page says scores are not required for admission;
// the CDS says UNR does not use them and marks them not considered, even if
// submitted, for fall 2027. Admission rests on the GPA requirement, so the
// policy is stated as test-blind, with scores used for scholarships and
// placement. Nothing here is profile-derived, an odds figure or a student
// record. Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Nevada, Reno Common Data Set 2025-2026',
        url: 'https://dataden.unr.edu/CommonDataSet',
        read: '2026-10-01',
    },
    freshman: {
        label: 'UNR Admissions, Freshman Admissions Guide',
        url: 'https://www.unr.edu/admissions/freshman',
        read: '2026-10-01',
    },
    coa: {
        label: 'UNR Financial Aid, Cost of Attendance (2026-2027 tables)',
        url: 'https://www.unr.edu/financial-aid/cost-of-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?182290-University-of-Nevada-Reno',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-nevada-reno',
    kbId: 'university_of_nevada_reno',
    name: 'University of Nevada, Reno',
    shortName: 'Nevada',
    aliases: ['UNR', 'Nevada'],
    location: 'Reno, NV',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UNR brand style); decoration only.
    branding: {
        primary: '#041E42',
        secondary: '#8A8D8F',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_nevada_reno_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 19136,
        admitted: 15908,
        enrolled: 3747,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UNR\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UNR has no early decision plan. Early action (apply by November 15) brings an earlier decision and early access to class registration, and does not commit you to attend. UNR publishes no early action counts.',
        source: 'freshman',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Final application deadline', value: 'April 7' },
        rows: [
            { plan: 'Early Action', deadline: 'November 15', decision: null, binding: false },
            { plan: 'Scholarship and financial aid deadline', deadline: 'February 15', decision: null, binding: false },
            { plan: 'Final application deadline', deadline: 'April 7', decision: null, binding: false },
        ],
        note: 'The application opens August 1, 2026, and there is no application fee for domestic freshmen. For the most financial aid and scholarships, send your application and required documents by February 15, 2027. To keep a scholarship offer, pay your deposit and complete the required questionnaire by June 15, 2027. UNR does not publish decision dates.',
        source: 'freshman',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'UNR does not require the SAT or ACT for admission. Freshman admission rests on a minimum 3.0 weighted academic GPA in core courses, and scores are not considered for admission. Scores are required for most first-year scholarships and can help with course placement.',
        source: 'freshman',
        cdsLine: 'The Common Data Set answers no to using SAT or ACT scores in admission decisions and marks them not considered for admission, even if submitted, for fall 2027 applicants.',
        cdsWhere: 'section C8A',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [9384, 29572] },
            { item: 'Fees', amounts: [1698, 1698] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1200, 1200] },
            { item: 'Housing (on campus)', amounts: [8464, 8464] },
            { item: 'Food', amounts: [7488, 7488] },
            { item: 'Personal', amounts: [1722, 1722] },
            { item: 'Transportation', amounts: [1334, 1334] },
        ],
        totals: [31290, 51478],
        note: 'UNR\'s budget for a full-time undergraduate (30 semester hours) living on campus, fall and spring. Students who borrow federal Direct Loans add $80 in loan fees. At the Western Undergraduate Exchange (WUE) tuition rate, tuition is $14,074 and the total is $35,980.',
        source: 'coa',
        where: 'the undergraduate Nevada resident, non-resident and WUE tables (full time, living on campus)',
        netPrice: {
            average: 13797,
            byIncome: [
                { income: '$0 to $30,000', amount: 11183 },
                { income: '$30,001 to $48,000', amount: 10966 },
                { income: '$48,001 to $75,000', amount: 12038 },
                { income: '$75,001 to $110,000', amount: 14825 },
                { income: 'Over $110,000', amount: 20329 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UNR\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
